import { z } from 'zod';

/**
 * @aiPurpose locked catalog of CRM Lead stage codes (#1076, extended by
 *   #1359). Seeded by `20260523020000_seed_crm_lead_stages` (codes 1-7)
 *   and `20260603160000_add_contact_phone_key_registry` (the `returned`
 *   code); admin may edit `displayName`, `taskTitle`, and
 *   `defaultDueMinutes` only.
 * @aiInvariant codes, kinds, and seeded order are LOCKED. Adding a new
 *   stage requires a paired backfill migration AND a coordinated
 *   schema rev. The canonical codes mirror the Prisma enum-like
 *   `LeadStage.code` catalog and the doc `docs/crm-lead-workspace.md`.
 *   `returned` (#1359) is an ACTIVE re-entry code that a system
 *   transition (`canSystemTransitionLeadStage`) may move a Lead into
 *   from any stage.
 */
export const LEAD_STAGE_CODES = [
  'new',
  'in_work',
  'postponed',
  'awaiting_payment',
  'returned',
  'not_suitable',
  'refusal',
  'paid',
] as const;
export type LeadStageCode = (typeof LEAD_STAGE_CODES)[number];
export const LeadStageCodeSchema = z.enum(LEAD_STAGE_CODES);

export const ACTIVE_LEAD_STAGE_CODES = [
  'new',
  'in_work',
  'postponed',
  'awaiting_payment',
  'returned',
] as const satisfies ReadonlyArray<LeadStageCode>;
export type ActiveLeadStageCode = (typeof ACTIVE_LEAD_STAGE_CODES)[number];

export const TERMINAL_LEAD_STAGE_CODES = [
  'not_suitable',
  'refusal',
  'paid',
] as const satisfies ReadonlyArray<LeadStageCode>;
export type TerminalLeadStageCode = (typeof TERMINAL_LEAD_STAGE_CODES)[number];

/**
 * @aiPurpose lifecycle class of a Lead stage. Mirrors the Prisma
 *   `LeadStageKind` enum.
 */
export enum LeadStageKind {
  ACTIVE = 'ACTIVE',
  TERMINAL = 'TERMINAL',
}
export const LeadStageKindSchema = z.nativeEnum(LeadStageKind);

const ACTIVE_SET: ReadonlySet<LeadStageCode> = new Set(ACTIVE_LEAD_STAGE_CODES);
const TERMINAL_SET: ReadonlySet<LeadStageCode> = new Set(TERMINAL_LEAD_STAGE_CODES);

export const isActiveLeadStageCode = (code: LeadStageCode): code is ActiveLeadStageCode =>
  ACTIVE_SET.has(code);
export const isTerminalLeadStageCode = (code: LeadStageCode): code is TerminalLeadStageCode =>
  TERMINAL_SET.has(code);

/**
 * @aiPurpose manager-facing stage transition graph (#1956 Phase 2). Any
 *   source stage may move to any different destination EXCEPT manual
 *   `returned` (system-entry only) and self. Terminal statuses are
 *   reversible lightweight flags (#1956 Phase 2) — e.g. `paid→in_work`
 *   and `not_suitable→awaiting_payment` are both allowed.
 * @aiInvariant `paid` requires a bound Client — the FK invariant is
 *   enforced at the service boundary in #1079, not in this lookup.
 * @aiInvariant `returned` is excluded as a manual destination (#1359).
 *   It is a system-entry-only stage reachable via
 *   `canSystemTransitionLeadStage`. A lead already IN `returned` may
 *   still transition OUT (e.g. returned→in_work, returned→paid) — only
 *   the destination is blocked here, not the source.
 */
export const canTransitionLeadStage = (from: LeadStageCode, to: LeadStageCode): boolean => {
  if (from === to) return false;
  if (to === 'returned') return false; // returned is system-entry only (canSystemTransitionLeadStage)
  return true;
};

/**
 * @aiPurpose privileged system stage-transition predicate (#1359). This is
 *   the system path that stamps a Lead as `returned` (the re-intake marker
 *   managers cannot set manually). Terminal→active manual transitions are
 *   now allowed via `canTransitionLeadStage` (#1956 Phase 2); this helper
 *   exists only for the system `returned` re-entry.
 * @aiInvariant the ONLY allowed destination is `returned`; every other
 *   target (including self-transition into `returned`) is rejected so a
 *   broadened system path cannot become a back door around
 *   `canTransitionLeadStage`. The wired call site is
 *   `apps/backend/src/modules/leads/lead-back-to-work.service.ts`.
 */
export const canSystemTransitionLeadStage = (from: LeadStageCode, to: LeadStageCode): boolean => {
  if (to !== 'returned') return false;
  if (from === 'returned') return false;
  return true;
};

export const LEAD_STAGE_DISPLAY_NAME_MAX = 128;
export const LEAD_STAGE_TASK_TITLE_MAX = 256;
export const LEAD_STAGE_DEFAULT_DUE_MIN = 0;
export const LEAD_STAGE_DEFAULT_DUE_MAX = 60 * 24 * 365; // one year in minutes.

/**
 * @aiPurpose wire projection of a `LeadStage` row.
 */
export const LeadStageSchema = z
  .object({
    code: LeadStageCodeSchema,
    kind: LeadStageKindSchema,
    displayName: z.string().min(1).max(LEAD_STAGE_DISPLAY_NAME_MAX),
    sortOrder: z.number().int().nonnegative(),
    taskTitle: z.string().min(1).max(LEAD_STAGE_TASK_TITLE_MAX).nullable(),
    defaultDueMinutes: z
      .number()
      .int()
      .min(LEAD_STAGE_DEFAULT_DUE_MIN)
      .max(LEAD_STAGE_DEFAULT_DUE_MAX)
      .nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type LeadStage = z.infer<typeof LeadStageSchema>;

/**
 * @aiPurpose payload admin sends to update a seeded stage row. The
 *   `code`, `kind`, and `sortOrder` are NOT editable.
 */
export const UpdateLeadStageInputSchema = z
  .object({
    displayName: z.string().min(1).max(LEAD_STAGE_DISPLAY_NAME_MAX).optional(),
    taskTitle: z.string().min(1).max(LEAD_STAGE_TASK_TITLE_MAX).nullable().optional(),
    defaultDueMinutes: z
      .number()
      .int()
      .min(LEAD_STAGE_DEFAULT_DUE_MIN)
      .max(LEAD_STAGE_DEFAULT_DUE_MAX)
      .nullable()
      .optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, {
    message: 'at least one editable field must be provided',
  });
export type UpdateLeadStageInput = z.infer<typeof UpdateLeadStageInputSchema>;
